// Centralised GTM dataLayer helper. The GTM container is installed in index.html;
// this module only pushes events to window.dataLayer — it loads no scripts.

const CHANNEL_MAP = {
  pr_distribution: "journalist_outreach",
  newswires: "newswire",
  guaranteed_placements: "guaranteed_placement",
};

// GA4 ecommerce event names. These require the ecommerce payload to be nested
// inside an `ecommerce` object (GTM's "Send Ecommerce data" setting only reads
// from there) and a preceding `{ ecommerce: null }` clear so stale items from
// the previous event never leak into the next one.
const ECOMMERCE_EVENTS = new Set([
  "view_item_list",
  "select_item",
  "view_item",
  "begin_checkout",
  "add_payment_info",
  "purchase",
  "add_to_cart",
  "remove_from_cart",
  "view_cart",
  "add_to_wishlist",
  "refund",
  "select_promotion",
  "view_promotion",
]);

// Fields that belong inside the GA4 `ecommerce` object, not at the top level.
const ECOMMERCE_FIELDS = [
  "items",
  "item_list_name",
  "item_list_id",
  "value",
  "currency",
  "transaction_id",
  "tax",
  "shipping",
  "coupon",
  "affiliation",
];

export function track(event, payload = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  if (ECOMMERCE_EVENTS.has(event)) {
    const ecommerce = {};
    const rest = {};
    for (const key of Object.keys(payload)) {
      if (ECOMMERCE_FIELDS.includes(key)) {
        ecommerce[key] = payload[key];
      } else {
        rest[key] = payload[key];
      }
    }
    // Clear the previous event's ecommerce object so stale items never leak.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...rest, ecommerce });
  } else {
    window.dataLayer.push({ event, ...payload });
  }
}

// Expose user-provided data (email) to GA4 for enhanced conversions.
// Mirrors gtag('set', 'user_data', ...) for a standalone gtag.js snippet; with
// the GTM container the email is also passed inside the purchase event payload.
export function setUserData({ email } = {}) {
  if (typeof window === "undefined") return;
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized) return;
  if (typeof window.gtag === "function") {
    try { window.gtag("set", "user_data", { email: normalized }); } catch {}
  }
}

export function userTypeFromRole(role) {
  return role === "reseller" || role === "reseller_sub_account" ? "reseller" : "customer";
}

export function channelOf(category) {
  return CHANNEL_MAP[category] || category;
}

// Build a GA4-style item from an order-flow Package entity.
export function packageToItem(p) {
  const price = Number(p.discount_price_usd != null ? p.discount_price_usd : p.price_usd) || 0;
  return {
    item_id: p.id || p.slug || p.name || "",
    item_name: p.name || "",
    item_category: channelOf(p.category),
    price,
    quantity: 1,
  };
}